import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoChevronDown } from 'react-icons/io5';
import { pages } from '@microsoft/teams-js';
import { toast } from 'react-hot-toast';

import { topTabs } from '../../utils/constants';
import { useEmails } from '../../context/EmailsProvider';
import { useTeams } from '../../context/TeamsProvider';
import {
  ENABLE_AI_CHAT,
  ENABLE_COMMUNICATION,
  ENABLE_WORKFLOWS,
  ENABLE_PROJECT_REGISTER,
} from '../../config/features';
import { useAccess } from '../../services/access';

const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID;

const Tabbar = ({ absolute = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const tabsContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftPos, setScrollLeftPos] = useState(0);

  // Drag-to-scroll handlers for desktop tabbar
  const handleMouseDown = (e) => {
    if (!tabsContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tabsContainerRef.current.offsetLeft);
    setScrollLeftPos(tabsContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !tabsContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - tabsContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    tabsContainerRef.current.scrollLeft = scrollLeftPos - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Convert vertical wheel scroll to horizontal scroll
  const handleWheel = (e) => {
    if (!tabsContainerRef.current) return;
    e.preventDefault();
    tabsContainerRef.current.scrollLeft += e.deltaY;
  };
  const { isTeamsInitialized } = useTeams();
  const { newEmailCount = 0, priorities = [] } = useEmails() || {};

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // calculating email notification badgeColor based on the highest priority
  const highestPriority =
    priorities.length > 0 ? Math.max(...priorities) : null;
  const badgeColor =
    highestPriority === 0 || highestPriority === 1
      ? 'bg-[#5652CF]'
      : highestPriority === 2 || highestPriority === 3
        ? 'bg-yellow-500'
        : highestPriority >= 4
          ? 'bg-red-600'
          : 'bg-gray-500';

  // Prefix match, not exact — /analytics/risks etc. should still highlight
  // the Analytics pill, the same way /chat/:taskId should highlight Chat.
  // matchPrefix covers tabs (like Analytics) whose nav target is one
  // specific sub-page but whose "active" range is the whole section.
  const isTabActive = (tab) => {
    const base = tab.matchPrefix || tab.link;
    return (
      location.pathname === base || location.pathname.startsWith(`${base}/`)
    );
  };
  const { can } = useAccess();
  const currentTab = topTabs.find(isTabActive);
  const visibleTabs = topTabs.filter((tab) => {
    if (!(ENABLE_AI_CHAT && can('chat:send')) && tab.link === '/chat')
      return false;
    if (!ENABLE_PROJECT_REGISTER && tab.matchPrefix === '/analytics')
      return false;
    if (!ENABLE_COMMUNICATION && tab.link === '/communication') return false;
    if (!ENABLE_WORKFLOWS && tab.link === '/workflow') return false;
    return true;
  });

  const handleTabClick = (tab) => {
    if (isMobile) {
      setIsExpanded(false);
    }
  };
  const handleTeamsTabSwitch = (tab) => {
    if (!isTeamsInitialized || !tab.entityId) return;

    const targetPageId = tab.entityId;

    if (pages.currentApp.isSupported()) {
      pages.currentApp
        .navigateTo({
          pageId: targetPageId,
        })
        .then(() => {
          toast.success(`Switched to ${tab.label} tab in Teams`);
        })
        .catch((error) => {
          toast.error(`Failed to switch to ${tab.label} tab in Teams`);
        });
    } else if (TEAMS_APP_ID) {
      pages
        .navigateToApp({
          appId: TEAMS_APP_ID,
          pageId: targetPageId,
        })
        .then(() => {})
        .catch((error) => {
          toast.error(`Failed to open ${tab.label} tab in Teams`);
        });
    } else {
      // No Teams app ID in this build: stay in this tab and route in-app.
      navigate(tab.link);
    }
  };

  return (
    <div
      className={`${
        absolute ? 'absolute top-2' : 'fixed top-5'
      } left-1/2 z-[50] -translate-x-1/2 transform`}
      ref={dropdownRef}
    >
      {/* Mobile Tabbar */}
      {isMobile ? (
        <div className="relative">
          {/* Current Tab Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="bg-3 br-1 inline-flex items-center gap-2 rounded-full border p-2 pr-3 transition-all duration-200"
          >
            <span className="relative flex items-center gap-1 rounded-full bg-primary-gradient px-2.5 py-1 text-xs font-semibold text-white">
              {currentTab && <currentTab.icon />}
              <span>{currentTab?.label || 'Select'}</span>
              {/* Notification Badge */}
              {currentTab?.label === 'Communication' && newEmailCount > 0 && (
                <div
                  className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-white ${badgeColor}`}
                >
                  {newEmailCount}
                </div>
              )}
            </span>
            <IoChevronDown
              className={`text-s-text transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isExpanded && (
            <div className="bg-3 br-1 absolute left-1/2 top-full mt-2 min-w-[200px] -translate-x-1/2 transform rounded-lg border py-2 shadow-lg">
              {visibleTabs.map((tab, index) =>
                isTeamsInitialized && tab.entityId ? (
                  <button
                    key={index}
                    onClick={() => handleTeamsTabSwitch(tab)}
                    className={`relative mx-2 flex w-full items-center gap-2 rounded-lg px-4 py-2 text-left transition-all duration-200 ${
                      isTabActive(tab)
                        ? 'bg-primary-gradient font-semibold text-white'
                        : 'text-s-text hover:bg-white hover:bg-opacity-10'
                    }`}
                  >
                    <tab.icon />
                    <span className="text-sm">{tab.label}</span>
                    {tab.label === 'Communication' && newEmailCount > 0 && (
                      <div
                        className={`absolute right-2 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-white ${badgeColor}`}
                      >
                        {newEmailCount}
                      </div>
                    )}
                  </button>
                ) : (
                  <Link
                    to={tab.link}
                    key={index}
                    onClick={() => handleTabClick(tab)}
                    className="block"
                  >
                    <div
                      className={`relative mx-2 flex items-center gap-2 rounded-lg px-4 py-2 transition-all duration-200 ${
                        isTabActive(tab)
                          ? 'bg-primary-gradient font-semibold text-white'
                          : 'text-s-text hover:bg-white hover:bg-opacity-10'
                      }`}
                    >
                      <tab.icon />
                      <span className="text-sm">{tab.label}</span>
                      {/* Notification Badge */}
                      {tab.label === 'Communication' && newEmailCount > 0 && (
                        <div
                          className={`absolute right-2 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-white ${badgeColor}`}
                        >
                          {newEmailCount}
                        </div>
                      )}
                    </div>
                  </Link>
                ),
              )}
            </div>
          )}
        </div>
      ) : (
        // eslint-disable-next-line
        <div
          ref={tabsContainerRef}
          className={`bg-3 br-1 hide-scrollbar inline-flex max-w-[700px] flex-nowrap gap-1 overflow-x-auto rounded-full border p-1 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onWheel={handleWheel}
        >
          {visibleTabs.map((tab, index) =>
            isTeamsInitialized && tab.entityId ? (
              <button
                key={index}
                onClick={() => handleTeamsTabSwitch(tab)}
                className={`relative flex min-h-5 flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border-0 px-2.5 py-1 transition-all duration-200 ${
                  isTabActive(tab)
                    ? 'text-p-text bg-primary-gradient font-semibold text-white'
                    : 'hover:text-s-text text-s-text bg-transparent font-normal'
                }`}
                style={{ fontSize: '0.6rem' }}
              >
                <tab.icon />
                <span className="text-xs">{tab.label}</span>
                {tab.label === 'Communication' && newEmailCount > 0 && (
                  <div
                    className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-white ${badgeColor}`}
                  >
                    {newEmailCount}
                  </div>
                )}
              </button>
            ) : (
              <Link to={tab.link} key={index}>
                <span
                  className={`relative flex min-h-5 flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border-0 px-2.5 py-1 transition-all duration-200 disabled:cursor-not-allowed ${
                    isTabActive(tab)
                      ? 'text-p-text bg-primary-gradient font-semibold text-white'
                      : 'hover:text-s-text text-s-text bg-transparent font-normal'
                  }`}
                  style={{ fontSize: '0.6rem' }}
                >
                  {<tab.icon />}
                  <span className="text-xs">{tab.label}</span>
                  {/* Notification Badge */}
                  {tab.label === 'Communication' && newEmailCount > 0 && (
                    <div
                      className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-white ${badgeColor}`}
                    >
                      {newEmailCount}
                    </div>
                  )}
                </span>
              </Link>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default Tabbar;
